define('home/collections/common/SelectiveCollection',[
    'jquery',
    'underscore',
    'backbone',
], function (
    $,
    _,
    Backbone
) {
    'use strict';

    /**
     * Builds a collection from a given ordered backer collection. Selects
     * only certain items to a certain limit.
     *
     * The given backing collection of models will be scanned in order, each
     * model checked for selectability. The first items that pass the
     * selectability test will be inserted into this collection, up to the
     * max number specified.
     *
     * If there are not enough items that pass the selectability test, there
     * will be less than the specified number of items shown.
     *
     * Initialization Options:
     *
     * {Collection} toSelect (required) - A collection of models that may be
     *     selected for this collection. These will be checked in order for
     *     selectability, and the top <maxSize> will be added to this collection.
     *
     * {function} shouldSelect (optional) - A function that takes an item from
     *     <toSelect> and returns a promise (or bool). If this promise is resolved, the
     *     item will be added to this collection. If the promise is rejected, the
     *     next item in <toSelect> will be checked for selectability. If not given,
     *     all items will be assumed to be selectable.
     *
     * {number} maxSize (optional) - The number of items from <toSelect> that
     *     should be selected (assuming there are enough that pass <shouldSelect>).
     *     If not given, all the items from <toSelect> that pass <shouldSelect> will
     *     be added to this collection.
     *
     * {function} order (optional) - The ordering to test the items in <toFeature> for
     *     featurability. Takes the collection and converts to an array of models.
     */
    var SelectiveCollection = Backbone.Collection.extend({
        initialize: function (models, options) {
            if (models)
                // If models is defined, even as an empty array, this collection will be reset
                // with the given models after this initialization, which will overwrite any
                // selected items that have already resolved and been added.
                throw new Error('SelectiveCollection should be initialized with undefined models to be allowed to populate itself from a given toSelect collection');

            if (!options || !options.toSelect || !(options.toSelect instanceof Backbone.Collection))
                throw new Error('SelectiveCollection must be initialized with a toSelect collection');

            this.toSelect = options.toSelect;
            this.shouldSelect = options.shouldSelect || this.shouldSelect;
            this.maxSize = options.maxSize;
            this.order = options.order || this.order;

            this.selectComplete = $.Deferred();

            if (this.toSelect.length)
                this.selectFrom(this.toSelect);
            else
                this.toSelect.once('sync', this.selectFrom, this);
        },

        selectFrom: function (collection) {
            if (!collection.length) {
                this.completeSelection();
                return;
            }

            this.maxSize = _.isNumber(this.maxSize) ?
                this.maxSize :
                collection.length;

            var stack = this.order(collection);
            this.resolveSelectability(stack.shift(), stack);
        },

        resolveSelectability: function (item, rest) {
            if (!item) {
                this.completeSelection();
                return;
            }

            var self = this;

            // Want to select items in the order they are checked for selectability. Should
            // only check 1 item at a time for selectability. Once selectability has been
            // resolved, can start checking the next item. Otherwise items may be selected
            // out of order if a later check resolves first.
            $.when(this.shouldSelect(item)).then(function (shouldSelect) {
                if (shouldSelect)
                    self.select(item);

                // If we need more items to fill this collection, check the next item in
                // order for selectibility
                if (self.length < self.maxSize)
                    self.resolveSelectability(rest.shift(), rest);
                else
                    self.completeSelection();
            });
        },

        /**
         * Returns a promise which resolves when the selection process is complete.
         * @returns {Promise}
         */
        isComplete: function () {
            return this.selectComplete.promise();
        },

        /**
         * Marks the selection as completed.
         */
        completeSelection: function () {
            this.selectComplete.resolve();
        },

        /**
         * Given an item, add it to this collection
         */
        select: function (model) {
            // if already selected the max number of items, don't do anything
            if (this.length >= this.maxSize) {
                this.completeSelection();
                return;
            }

            this.add(model);
        },

        /**
         * Default shouldSelect function (always select) if not passed
         * via `options.shouldSelect` in initialization
         * @returns {boolean} should the model passed in be selected
         */
        shouldSelect: function (/* model */) {
            return true;
        },

        /**
         * Default order function if not passed via `options.order` in
         * initialization - simply returns the array of models in their
         * current order in the collection
         * @param  {Collection} collection the toSelect collection
         * @returns {Array}            an array of Models in selection order
         */
        order: function (collection) {
            return collection.toArray();
        },
    });

    return SelectiveCollection;
});

